import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../auth/AuthContext";
import { ModelMode } from "../config/modelConfig";
import { Workflow } from "../config/workflowConfig";
import {
  deleteConversationApi,
  listConversations,
  upsertConversation as upsertConversationApi,
} from "../services/api";
import { apiToConversation, conversationToApi } from "../services/conversationMapper";
import { conversationTitle } from "../services/conversationStorage";
import { Conversation, ChatMessage } from "../types/chat";

const SAVE_DEBOUNCE_MS = 700;

function emptyConversation(
  modelMode: ModelMode,
  workflow: Workflow = "chat",
  conversationId?: string,
): Conversation {
  const now = new Date().toISOString();
  return {
    conversationId: conversationId ?? uuidv4(),
    selectedWorkflow: workflow,
    selectedModelMode: modelMode,
    selectedTroubleshootingOptions: [],
    messages: [],
    createdAt: now,
    updatedAt: now,
  };
}

function dedupeConversations(conversations: Conversation[]): Conversation[] {
  const byId = new Map<string, Conversation>();
  for (const c of conversations) {
    const existing = byId.get(c.conversationId);
    if (!existing || c.updatedAt > existing.updatedAt) {
      byId.set(c.conversationId, c);
    }
  }
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

export function useConversations(initialModelMode: ModelMode) {
  const { user, loading: authLoading } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [hydrated, setHydrated] = useState(false);
  const saveTimerRef = useRef<number | null>(null);
  const lastSavedRef = useRef<string>("");
  // Deleted ids must never be re-saved: a debounced write already in flight
  // would otherwise recreate the row on the server.
  const deletedIdsRef = useRef<Set<string>>(new Set());

  // Load from API after auth is ready.
  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;

    const bootstrapEmpty = () => {
      const conv = emptyConversation(initialModelMode);
      setConversations([conv]);
      setActiveId(conv.conversationId);
      setHydrated(true);
    };

    if (!user) {
      bootstrapEmpty();
      return;
    }

    (async () => {
      try {
        const raw = await listConversations();
        if (cancelled) return;
        const mapped = (Array.isArray(raw) ? raw : []).map(apiToConversation);
        const list = dedupeConversations(mapped);
        if (list.length === 0) {
          bootstrapEmpty();
          return;
        }
        setConversations(list);
        setActiveId(list[0].conversationId);
        setHydrated(true);
      } catch (err) {
        console.error("Failed to load conversations", err);
        if (!cancelled) bootstrapEmpty();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading, initialModelMode]);

  // Debounced upsert of the active conversation.
  useEffect(() => {
    if (!hydrated || !user || !activeId) return;

    const current = conversations.find((c) => c.conversationId === activeId);
    if (!current) return;
    if (current.messages.length === 0) return;
    if (deletedIdsRef.current.has(current.conversationId)) return;

    const payload = conversationToApi(current);
    const fingerprint = JSON.stringify(payload);
    if (fingerprint === lastSavedRef.current) return;

    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = window.setTimeout(async () => {
      if (deletedIdsRef.current.has(current.conversationId)) return;
      try {
        await upsertConversationApi(current.conversationId, payload);
        lastSavedRef.current = fingerprint;
      } catch (err) {
        console.error("Failed to save conversation", err);
      }
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, [conversations, activeId, hydrated, user]);

  const activeConversation = useMemo(() => {
    const found = conversations.find((c) => c.conversationId === activeId);
    if (found) return found;
    return emptyConversation(initialModelMode, "chat", activeId || undefined);
  }, [conversations, activeId, initialModelMode]);

  const patchActiveConversation = useCallback(
    (patch: Partial<Conversation> | ((current: Conversation) => Partial<Conversation>)) => {
      setConversations((prev) => {
        const deduped = dedupeConversations(prev);
        const idx = deduped.findIndex((c) => c.conversationId === activeId);
        if (idx === -1) {
          const base = emptyConversation(initialModelMode, "chat", activeId || undefined);
          const created = {
            ...base,
            ...(typeof patch === "function" ? patch(base) : patch),
            updatedAt: new Date().toISOString(),
          };
          return dedupeConversations([created, ...deduped]);
        }
        const current = deduped[idx];
        const patchValue = typeof patch === "function" ? patch(current) : patch;
        const updated = {
          ...current,
          ...patchValue,
          updatedAt: new Date().toISOString(),
        };
        const next = [...deduped];
        next[idx] = updated;
        return next;
      });
    },
    [activeId, initialModelMode]
  );

  const startNewChat = useCallback(
    (modelMode: ModelMode, workflow: Workflow = "chat") => {
      const conv = emptyConversation(modelMode, workflow);
      setConversations((prev) => dedupeConversations([conv, ...prev]));
      setActiveId(conv.conversationId);
      lastSavedRef.current = "";
      return conv;
    },
    []
  );

  const selectConversation = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  const deleteConversation = useCallback(
    async (id: string) => {
      // Stop a queued save before it can recreate what we are deleting.
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      deletedIdsRef.current.add(id);

      if (user) {
        try {
          await deleteConversationApi(id);
        } catch (err: unknown) {
          const status =
            err && typeof err === "object" && "response" in err
              ? (err as { response?: { status?: number } }).response?.status
              : undefined;
          // 404 means it was never persisted (empty chat) or is already gone,
          // so the local copy should still be removed.
          if (status !== 404) {
            console.error("Failed to delete conversation", err);
            deletedIdsRef.current.delete(id);
            return;
          }
        }
      }

      const remaining = conversations.filter((c) => c.conversationId !== id);

      if (remaining.length === 0) {
        const conv = emptyConversation(initialModelMode);
        lastSavedRef.current = "";
        setConversations([conv]);
        setActiveId(conv.conversationId);
        return;
      }

      setConversations(remaining);
      if (id === activeId) {
        lastSavedRef.current = "";
        setActiveId(remaining[0].conversationId);
      }
    },
    [user, activeId, initialModelMode, conversations]
  );

  const updateActiveMeta = useCallback(
    (patch: Partial<Conversation>) => {
      patchActiveConversation(patch);
    },
    [patchActiveConversation]
  );

  const appendUserMessage = useCallback(
    (userMsg: ChatMessage) => {
      patchActiveConversation((current) => ({
        messages: [...current.messages, userMsg],
      }));
    },
    [patchActiveConversation]
  );

  const appendMessages = useCallback(
    (userMsg: ChatMessage, assistantMsg: ChatMessage) => {
      patchActiveConversation((current) => {
        const ids = new Set(current.messages.map((m) => m.id));
        const nextMessages = [...current.messages];
        if (!ids.has(userMsg.id)) nextMessages.push(userMsg);
        if (!ids.has(assistantMsg.id)) nextMessages.push(assistantMsg);
        return { messages: nextMessages };
      });
    },
    [patchActiveConversation]
  );

  return {
    conversations,
    activeConversation,
    activeId,
    hydrated,
    startNewChat,
    selectConversation,
    deleteConversation,
    updateActiveMeta,
    appendUserMessage,
    appendMessages,
    conversationTitle,
  };
}
